/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { Params } from '@angular/router';
import { PositionService } from '@services/position/position.service';
import { AppService } from '@services/app/app.service';
import { ApiService } from '@api/services/api/api.service';
import {
  WizardDTO,
  WizardStepDTO,
  WizardStepNextOutputDTO
} from '@api/models/';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, throwError, forkJoin } from 'rxjs';
import { map, filter, mergeMap, delay } from 'rxjs/operators';
import { StepDataModel, StepsIndexesModel, Wizard } from '@core/models';

@Injectable({
  providedIn: 'root'
})
export class WizardService {
  /**
   * outcome step aperto
   */
  static openStepOutcome = 0;

  /**
   * outcome step chiuso
   */
  static closedStepOutcome = 2;

  /**
   * subject per gestire gli aggiornamenti del wizard
   */
  private wizard$: BehaviorSubject<Wizard> = new BehaviorSubject<Wizard>(null);

  /**
   * subject per gestire gli aggiornamenti del step in lavorazione
   */
  private activeStep$: BehaviorSubject<StepDataModel> =
    new BehaviorSubject<StepDataModel>(null);

  /**
   * mappa degli indici degli step
   */
  private stepsIndexes: StepsIndexesModel;

  constructor(
    private apiService: ApiService,
    private appService: AppService,
    private positionService: PositionService
  ) {}

  /**
   * carica il wizard
   */
  loadWizard(wizardCode: string): Observable<boolean> {
    return this.getWizardSource(wizardCode).pipe(
      map(res => this.setWizard(res, wizardCode)),
      map(() => true)
    );
  }

  /**
   * ricarica i dati del wizard attuale
   */
  reloadWizard(): Observable<boolean> {
    const wizardCode = this.getWizardCode();

    if (this.isCodeOfDummyWizard(wizardCode)) {
      this.pingWizard();
      return of(true);
    } else {
      return this.loadWizard(wizardCode);
    }
  }

  /**
   * seleziona lo step da lavorare e aggiorna il relativo subject
   */
  loadActiveStep(stepCode: string, params?: Params): Observable<boolean> {
    const step = this.findStepByCode(stepCode);

    if (step && !this.isStepWorkable(step)) {
      return throwError(`wrn.step_already_finished`);
    }

    try {
      this.setActiveStep(step, params);
    } catch (err) {
      return throwError(err);
    }

    return of(true);
  }

  /**
   * completa con outcome l'active step;
   * è possibile specificare un'azione preliminare da eseguire prima
   * di effettuare la chiusura del task
   */
  completeActiveStep(
    preliminaryAction?: () => Observable<any>
  ): Observable<WizardStepNextOutputDTO> {
    const activeStep = this.getActiveStep();

    if (!activeStep) {
      return throwError('wrn.not_existing_step');
    }

    let output: WizardStepNextOutputDTO;

    return this.runActionOrSkip<any>(preliminaryAction).pipe(
      mergeMap(() => this.completeStep(activeStep.stepCode)),
      map(res => (output = res)),
      mergeMap(() => this.reloadWizard()),
      map(() => output)
    );
  }

  /**
   * completa gli step ancora aperti precedenti allo step code
   */
  completeStepsBefore(stepCode: string): Observable<boolean> {
    const allSteps = this.getWizardSteps();
    const stepIndex = allSteps.findIndex(x => x.stepCode === stepCode);

    if (stepIndex < 0) {
      return of(false);
    }

    const prevOpenSteps = allSteps.filter(
      (step, index) => this.isStepOpen(step) && index < stepIndex
    );

    if (prevOpenSteps.length === 0) {
      return of(true);
    }

    return forkJoin(prevOpenSteps.map(x => this.completeStep(x.stepCode))).pipe(
      mergeMap(() => this.reloadWizard())
    );
  }

  /**
   * completa lo step richiesto
   */
  completeStep(stepCode: string): Observable<WizardStepNextOutputDTO> {
    if (this.isCodeOfDummyWizard(this.getWizardCode())) {
      return this.completeStepAsDummy(stepCode);
    } else {
      return this.completeStepAsEffective(stepCode).pipe(
        mergeMap(res => this.evaluateNextOutput(res))
      );
    }
  }

  /**
   * interrompe il wizard con la reason indicata
   */
  stopWithReason(reason: string): Observable<boolean> {
    return this.positionService.withdrawPosition(reason).pipe(map(() => true));
  }

  /**
   * restituisce osservabile per aggiornamenti wizard;
   * emetto con delay per assicurare la sincronizzazione degli indexes
   */
  getWizardWhenChange(): Observable<Wizard> {
    return this.wizard$.asObservable().pipe(
      delay(1),
      filter(x => !!x)
    );
  }

  /**
   * restituisce osservabile per gli aggiornamenti dello step attivo;
   * emetto con delay per assicurare la sincronizzazione degli indexes
   */
  getActiveStepWhenChange(): Observable<WizardStepDTO> {
    return this.activeStep$.asObservable().pipe(
      delay(1),
      filter(x => !!x && !!x.step),
      map(x => x.step)
    );
  }

  /**
   * restituisce dati wizard attuali
   */
  getWizard(): Wizard {
    return this.wizard$.getValue();
  }

  /**
   * restituisce il codide del wizard attuale
   */
  getWizardCode(): string {
    const wizard = this.getWizard();
    return wizard && wizard.data.wizardCode;
  }

  /**
   * restituisce dati dell'active step attuale
   */
  getActiveStep(): WizardStepDTO {
    const value = this.activeStep$.getValue();

    return value && value.step;
  }

  /**
   * restituisce dati dell'active step attuale
   */
  getActiveStepParams(): Params {
    const value = this.activeStep$.getValue();

    return value && value.params;
  }

  /**
   * restituisce la lista di step caricati nel wizard
   */
  getWizardSteps(): WizardStepDTO[] {
    const wizard = this.getWizard();
    return (wizard && wizard.data.steps) || [];
  }

  /**
   * restituisce la mappa degli indici degli step
   */
  getStepsIndexes(): StepsIndexesModel {
    if (!!this.stepsIndexes) {
      return { ...this.stepsIndexes };
    }

    return {
      firstOpen: -1,
      lastCompleted: -1,
      active: -1,
      next: -1,
      prev: -1
    };
  }

  /**
   * trova lo step corrispondente al codice riciesto
   */
  findStepByCode(stepCode: string): WizardStepDTO {
    return this.getWizardSteps().find(x => x.stepCode === stepCode);
  }

  /**
   * trova lo step corrispondente allì'index richiesto
   */
  findStepByIndex(index: number): WizardStepDTO {
    return this.getWizardSteps()[index] || undefined;
  }

  /**
   * trova l'index dello step attivo
   */
  findActiveStepIndex(): number {
    const activeStep = this.getActiveStep();

    return this.getWizardSteps().findIndex(
      x => x.stepCode === (activeStep && activeStep.stepCode)
    );
  }

  /**
   * trova l'index dello step successivo a quello attivo
   */
  findNextStepIndex(): number {
    const activeStepIndex = this.findActiveStepIndex();

    if (
      activeStepIndex >= 0 &&
      activeStepIndex < this.getWizardSteps().length - 1
    ) {
      return activeStepIndex + 1;
    } else {
      return -1;
    }
  }

  /**
   * trova l'index dello step precedente a quello attivo
   */
  findPrevStepIndex(): number {
    const activeStepIndex = this.findActiveStepIndex();

    if (activeStepIndex > 0) {
      return activeStepIndex - 1;
    } else {
      return -1;
    }
  }

  /**
   * trova l'index del primo step rimasto aperto
   */
  findFirstOpenStepIndex(): number {
    return this.getWizardSteps().findIndex(x => this.isStepOpen(x));
  }

  /**
   * trova l'index dell'ultimo step completato
   */
  findLastCompletedStepIndex(): number {
    return this.getWizardSteps()
      .map(x => this.isStepClosed(x))
      .lastIndexOf(true);
  }

  /**
   * determina se è possibile lavorare lo step
   */
  isStepWorkable(step: WizardStepDTO): boolean {
    return (
      this.isStepOpen(step) || !this.appService.getConfig().wizard.avoidBack
    );
  }

  /**
   * determina se lo step risulta aperto
   */
  isStepOpen(step: WizardStepDTO): boolean {
    return step && Number(step.status) === WizardService.openStepOutcome;
  }

  /**
   * determina se lo step risulta chiuso
   */
  isStepClosed(step: WizardStepDTO): boolean {
    return step && Number(step.status) === WizardService.closedStepOutcome;
  }

  /**
   * carica i dati del wizard
   */
  private getWizardSource(wizardCode: string): Observable<WizardDTO> {
    if (!wizardCode) {
      return throwError('wrn.wizard_loading_error');
    }

    if (this.isCodeOfDummyWizard(wizardCode)) {
      return this.getDummyWizard(wizardCode);
    } else {
      return this.getEffectiveWizard(wizardCode);
    }
  }

  /**
   * restituiste il wizard simulato corrispondente al wizard code
   */
  private getDummyWizard(wizardCode: string): Observable<WizardDTO> {
    const dummyWizard = this.getDummyWizardsConfig()[wizardCode];

    if (!dummyWizard) {
      return throwError('wrn.wizard_loading_error');
    }

    const dummyWizardCopy = JSON.parse(JSON.stringify(dummyWizard));

    return of(dummyWizardCopy);
  }

  /**
   * carica i dati del wizard effettivo corrispondente al wizard code
   */
  private getEffectiveWizard(wizardCode: string): Observable<WizardDTO> {
    const positionId = this.positionService.getPositionId();

    return this.apiService.getWizard(positionId, wizardCode);
  }

  /**
   * completa lo step su wizard effettivo
   */
  private completeStepAsEffective(
    stepCode: string
  ): Observable<WizardStepNextOutputDTO> {
    const closingMode = this.appService.getConfig().wizard.closingMode;

    if (closingMode === 'workflow') {
      return this.completeStepAsEffectiveWorkflow(stepCode);
    } else {
      return this.completeStepAsEffectiveWizard(stepCode);
    }
  }

  /**
   * completa lo step su wizard effetivo con modalità di chiusura normale
   */
  private completeStepAsEffectiveWizard(
    stepCode: string
  ): Observable<WizardStepNextOutputDTO> {
    const positionId = this.positionService.getPositionId();

    return this.apiService.putWizardStepNext(
      positionId,
      this.getWizardCode(),
      stepCode
    );
  }

  /**
   * completa lo step su wizard effetivo con modalità di chiusura workflow
   */
  private completeStepAsEffectiveWorkflow(
    stepCode: string
  ): Observable<WizardStepNextOutputDTO> {
    const positionId = this.positionService.getPositionId();

    return this.apiService.putWorkflowTaskClose(positionId, stepCode);
  }

  /**
   * completa lo step su wizard simulato
   */
  private completeStepAsDummy(
    stepCode: string
  ): Observable<WizardStepNextOutputDTO> {
    const step = this.wizard$.value.data.steps.find(
      x => x.stepCode === stepCode
    );

    if (!!step) {
      step.status = WizardService.closedStepOutcome;
    }

    return of({ outcome: 'OK' });
  }

  /**
   * valuta i messaggi contenuti nella next del wizard per determinare
   * se è presente un errore che non consente di passare allo step successivo
   */
  private evaluateNextOutput(
    output: WizardStepNextOutputDTO
  ): Observable<WizardStepNextOutputDTO> {
    const messageCode = output.messages && output.messages[0];

    if (
      !!messageCode &&
      ((messageCode === 'SOSP' && !output.outcome) ||
        messageCode === 'CQSB020' ||
        messageCode === 'CQSBANT' ||
        messageCode === 'call.bslight.ko')
    ) {
      return throwError('leave');
    } else {
      return of(output);
    }
  }

  /**
   * esegue l'azione se definita oppure risolve con risultato vuoto
   */
  private runActionOrSkip<T>(action?: () => Observable<T>): Observable<T> {
    if (typeof action !== 'function') {
      return of<T>(null);
    } else {
      return action();
    }
  }

  /**
   * stabilisce se al wizard code corrisponde ad un wizard simulato
   */
  private isCodeOfDummyWizard(wizardCode: string): boolean {
    return !!this.getDummyWizardsConfig()[wizardCode];
  }

  /**
   * restituisce la configurazione dei dummy wizards
   */
  private getDummyWizardsConfig(): { [code: string]: WizardDTO } {
    return this.appService.getConfig().wizard.dummyWizards || {};
  }

  /**
   * imposta il wizard e notifica l'aggiornamento
   */
  private setWizard(wizard: WizardDTO, code: string): void {
    this.wizard$.next(new Wizard(wizard, code));

    this.updateStepsIndexes();
  }

  /**
   * imposta il wizard con i dati attuali e notifica l'aggiornamento
   */
  private pingWizard(): void {
    this.wizard$.next(this.wizard$.value);

    this.updateStepsIndexes();
  }

  /**
   * imposta l'active step e notifica l'aggiornamento
   */
  private setActiveStep(step: WizardStepDTO, params?: Params): void {
    if (!step) {
      this.activeStep$.next(null);
      throw new Error('Impossibile inizializzare lo step attivo');
    }

    this.activeStep$.next({
      step: { ...step },
      params: (params && { ...params }) || undefined
    });

    this.updateStepsIndexes();
  }

  /**
   * aggiorna gli indici degli step
   */
  private updateStepsIndexes(): void {
    this.stepsIndexes = {
      firstOpen: this.findFirstOpenStepIndex(),
      lastCompleted: this.findLastCompletedStepIndex(),
      active: this.findActiveStepIndex(),
      next: this.findNextStepIndex(),
      prev: this.findPrevStepIndex()
    };
  }
}
